import { useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Alert,
  Divider,
  CircularProgress
} from '@mui/material';
import { auth } from '../utils/auth';

function SignUp() {
  const theme = useTheme();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    general: ''
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const validatePassword = (password) => {
    // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
    return password.length >= 8 &&
           /[A-Z]/.test(password) &&
           /[a-z]/.test(password) &&
           /[0-9]/.test(password);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear specific field error when typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {
      email: '',
      password: '',
      confirmPassword: '',
      general: ''
    };
    let isValid = true;

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      isValid = false;
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (!validatePassword(formData.password)) {
      newErrors.password = 'Password must be at least 8 characters with uppercase, lowercase, and a number';
      isValid = false;
    }

    // Confirm password validation
    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setErrors({
        email: '',
        password: '',
        confirmPassword: '',
        general: ''
      });
      
      await auth.register(formData.email, formData.password);
      
      // Registration successful
      setSuccess(true);
      
      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Account created successfully! Please check your email for verification.' 
          } 
        });
      }, 2000);
      
    } catch (err) {
      console.error('Registration error:', err);
      
      // Handle different error cases
      if (err.message.includes('User already registered')) {
        setErrors(prev => ({ ...prev, email: 'This email is already registered' }));
      } else if (err.message.includes('invalid email')) {
        setErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
      } else if (err.message.includes('password')) {
        setErrors(prev => ({ ...prev, password: 'Password is too weak' }));
      } else {
        setErrors(prev => ({ 
          ...prev, 
          general: err.message || 'An error occurred during signup. Please try again.' 
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper 
        sx={{ 
          p: { xs: 3, md: 4 }, 
          borderRadius: 2, 
          boxShadow: 3,
          background: theme.palette.background.paper
        }}
      >
        <Typography 
          variant="h3" 
          gutterBottom 
          textAlign="center"
          sx={{
            fontWeight: 700,
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 2
          }}
        >
          Create Account
        </Typography>
        <Typography color="text.secondary" textAlign="center" sx={{ mb: 4 }}>
          Join IX-Study to start your AI-powered learning journey
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          This feature is currently disabled. Sign up functionality is temporarily unavailable.
        </Alert>

        {errors.general && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.general}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Account created successfully! Redirecting to login...
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              required
              autoComplete="email"
              error={!!errors.email}
              helperText={errors.email}
              disabled={true}
            />
            <TextField
              fullWidth
              label="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="new-password"
              error={!!errors.password}
              helperText={errors.password}
              disabled={true}
            />
            <TextField
              fullWidth
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              autoComplete="new-password"
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              disabled={true}
            />
            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={true}
              sx={{
                py: 1.5,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
                '&:hover': {
                  background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                }
              }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Sign Up'}
            </Button>
          </Stack>
        </form>

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ my: 2 }}>
            <Typography color="text.secondary" fontSize="0.875rem">
              Already have an account?
            </Typography>
          </Divider>
          <Box sx={{ textAlign: 'center' }}>
            <Button 
              component={Link} 
              to="/login" 
              variant="outlined" 
              sx={{ minWidth: 120 }}
            >
              Sign In
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 4, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            By signing up, you agree to our 
            <Link to="/terms" style={{ marginLeft: '4px', marginRight: '4px' }}>
              Terms of Service
            </Link>
            and
            <Link to="/privacy" style={{ marginLeft: '4px' }}>
              Privacy Policy
            </Link>
          </Typography>
        </Box>

        
        
        

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={true}
            sx={{ minWidth: 120 }}
          >
            Sign Up
          </Button>
        </Box>

        {errors.general && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {errors.general}
          </Alert>
        )}
      </Paper>
    </Container>
  );
}

export default SignUp;
